import React, { useMemo, FC } from 'react';
import { useQuery } from '@apollo/client';

import Jobs from './Jobs';
import Skeleton from './JobsSkeleton';
import JobsEmptyState from '../JobsEmptyState';

import Error from '@xingternal/pages-warning';
import {
  ModuleHeader,
  useEditContext,
  usePageContext,
} from '@xingternal/pages-common';
import { getTrackingUrl } from '@xingternal/pages-tracking';
import { EntityPageContractType } from '@xingternal/pages-graphql-types';
import { checkBookmarkState } from '../utils';
import { JobsContext } from '../JobsContext';

import JOBS_QUERY from '../JobsQuery.graphql';
import {
  type JobCard,
  type JobQueryType,
  type JobSearchByQueryType,
} from '../types';
import { type JobsQueryVariables } from '../JobsQuery.gql-types';
import { type ShowMoreTypes } from '@xingternal/pages-common/components/ModuleHeader/ModuleHeader';

const MINIMUM_JOBS_FOR_DETAIL_LINK = 4;
const LIST_LIMIT = 12;
const LIST_SORT = 'date/desc';

const prepareCards = (jobsInfo: Array<JobQueryType>) =>
  jobsInfo.reduce((previous: Array<JobCard>, current: JobQueryType) => {
    const { jobDetail } = current;
    if (!jobDetail) return previous;
    previous.push({
      id: jobDetail.id,
      companyName: jobDetail.companyInfo?.company?.companyName,
      companyLogo: jobDetail.companyInfo?.company?.logos?.logo96px,
      bookmarked: checkBookmarkState(
        jobDetail.userInteractions?.bookmark?.state
      ),
      title: jobDetail.title,
      activatedAt: jobDetail.activatedAt
        ? new Date(jobDetail.activatedAt)
        : new Date(),
      match: jobDetail.userScore?.scoreRounded
        ? Math.round(Number(jobDetail.userScore?.scoreRounded) * 100)
        : 0,
      location: `${jobDetail.location?.city}, ${jobDetail.location?.country?.localizationValue}`,
      link: jobDetail.url,
    });
    return previous;
  }, []);

type JobsContainerProps = {
  pageSlug: string;
  companyId: string;
};
const JobsContainer: FC<JobsContainerProps> = ({ pageSlug, companyId }) => {
  const { pageContext } = usePageContext();
  const { isEditing } = useEditContext();

  const isEBP = pageContext.contractType === EntityPageContractType.Paid;

  const shouldShowAllJobsAds = isEBP;

  const jobsQueryVariables = useMemo<JobsQueryVariables>(() => {
    const variables: JobsQueryVariables = {
      limit: LIST_LIMIT,
      sort: LIST_SORT,
      returnAggregations: false,
      query: {
        filter: {
          company: { id: [companyId] },
        },
      },
    };

    if (!shouldShowAllJobsAds && variables.query.filter) {
      variables.query.filter.publishToCompany = true;
    }

    return variables;
  }, [companyId, shouldShowAllJobsAds]);

  const { data, loading, error, refetch } = useQuery<
    JobSearchByQueryType,
    JobsQueryVariables
  >(JOBS_QUERY, {
    variables: jobsQueryVariables,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const { total, collection } = data?.jobSearchByQuery || {};

  const totalJobs = total || 0;
  const hasJobs = collection ? collection.length > 0 : false;
  const jobsInfo = collection;

  const showMoreProps = useMemo<ShowMoreTypes>(() => {
    const hasMinimumJobs = totalJobs > MINIMUM_JOBS_FOR_DETAIL_LINK;

    const showMoreProps: ShowMoreTypes = {
      loading,
    };

    if (hasMinimumJobs) {
      const link = `${pageContext.basePath}/${pageSlug}/jobs`;

      showMoreProps['data-testid'] = 'JOBS_SHOWMORE_LINK';
      showMoreProps.to = link;
      showMoreProps.onClick = (ev: MouseEvent) => {
        ev.preventDefault();
        const trakingLink = getTrackingUrl({
          to: link,
          trackingKey: 'trackingKey',
        });
        window.open(trakingLink);
      };
      showMoreProps.copyKey = 'EP_JOBS_LINK';
      showMoreProps.number = totalJobs;
    }

    return showMoreProps;
  }, [pageContext.basePath, pageSlug, totalJobs, loading]);

  const showErrorJobs = error && !hasJobs;
  const showEmptyState = !hasJobs && !error && !loading;

  return (
    <>
      <ModuleHeader
        headlineCopyKey="EP_JOBS_HEADLINE"
        showMore={showMoreProps}
        externalEditInfo={
          !loading && isEditing
            ? {
                copyKey: 'EP_JOBS_EDIT_HINT',
                cyTestId: 'EDIT_HINT_JOBS',
              }
            : null
        }
      />
      {loading && <Skeleton />}
      {hasJobs && (
        <div data-testid={'JOBS_MODULE'}>
          <JobsContext.Provider
            value={{
              variables: jobsQueryVariables,
              query: JOBS_QUERY,
            }}
          >
            <Jobs jobs={prepareCards(jobsInfo || [])} />
          </JobsContext.Provider>
        </div>
      )}
      {showErrorJobs && (
        <div data-testid={'errorContainer'}>
          <Error
            headerText="EP_ERROR_HEADER"
            bodyText="EP_ERROR_BODY"
            buttonText="EP_ERROR_RELOAD_CTA"
            onClick={() => refetch()}
          />
        </div>
      )}
      {showEmptyState && (
        <div data-testid={'JOBS_MODULE'}>
          <JobsEmptyState />
        </div>
      )}
    </>
  );
};

export default JobsContainer;
